import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import '../common/Modal.css';

export function SwitchRoleModal({
    isOpen,
    onClose,
    roles,
    currentRole,
    originalRole,
    onSubmit,
    submitting,
}) {
    const roleOptions = useMemo(
        () => roles.filter((role) => role && role !== originalRole),
        [roles, originalRole]
    );

    const [selectedRole, setSelectedRole] = useState('');
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setSelectedRole((prev) => {
            if (prev && roleOptions.includes(prev)) {
                return prev;
            }
            return roleOptions.length ? roleOptions[0] : '';
        });
        setError(null);
    }, [isOpen, roleOptions]);

    if (!isOpen) {
        return null;
    }

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!selectedRole) {
            setError('Select a role to impersonate');
            return;
        }
        setError(null);
        try {
            await onSubmit(selectedRole);
        } catch (err) {
            setError(err?.message || 'Unable to switch role.');
        }
    };

    return (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={(event) => event.target === event.currentTarget && !submitting && onClose()}>
            <div className="modal" onClick={(event) => event.stopPropagation()}>
                <div className="modal__header">
                    <h3>Switch role</h3>
                    <button type="button" className="modal__close" onClick={onClose} disabled={submitting} aria-label="Close switch role modal">
                        ×
                    </button>
                </div>
                <form className="modal__form" onSubmit={handleSubmit}>
                    <p style={{ marginTop: 0, color: '#475569' }}>
                        {`Signed in as ${originalRole}. ${currentRole !== originalRole ? `Currently acting as ${currentRole}.` : 'Choose a role to test permissions.'}`}
                    </p>

                    {roleOptions.length ? (
                        <div className="form-field">
                            <label htmlFor="switch-role-select" className="form-label">
                                Select role
                            </label>
                            <div className="form-input-group">
                                <select
                                    id="switch-role-select"
                                    value={selectedRole}
                                    onChange={(event) => setSelectedRole(event.target.value)}
                                    disabled={submitting}
                                >
                                    {roleOptions.map((role) => (
                                        <option key={role} value={role}>
                                            {role}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    ) : (
                        <div className="issue-reports-empty" style={{ marginBottom: '1rem' }}>
                            No alternative roles available.
                        </div>
                    )}

                    {error ? (
                        <div className="form-error" role="alert" style={{ marginBottom: '1rem' }}>
                            {error}
                        </div>
                    ) : null}

                    <div className="modal__footer">
                        <button type="button" className="secondary-btn" onClick={onClose} disabled={submitting}>
                            Cancel
                        </button>
                        <button type="submit" className="primary-btn" disabled={submitting || !roleOptions.length}>
                            {submitting ? 'Switching…' : 'Switch role'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

SwitchRoleModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    roles: PropTypes.arrayOf(PropTypes.string),
    currentRole: PropTypes.string,
    originalRole: PropTypes.string,
    onSubmit: PropTypes.func.isRequired,
    submitting: PropTypes.bool,
};

SwitchRoleModal.defaultProps = {
    roles: [],
    currentRole: '',
    originalRole: '',
    submitting: false,
};
